import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  setDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { db, isFirebaseConfigured } from "../firebase";

/* Every function below is a no-op / empty-result when Firebase is not
   configured, so components can call them unconditionally and simply
   render their existing "nothing here yet" state. */

/* ---------------- Testimonials ---------------- */

export function subscribeTestimonials(onData) {
  if (!isFirebaseConfigured) {
    onData([]);
    return () => {};
  }
  const q = query(collection(db, "testimonials"), orderBy("createdAt", "desc"));
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => onData([])
  );
}

export async function submitTestimonial({ name, context, area, quote, company }) {
  if (!isFirebaseConfigured) {
    throw new Error("not-configured");
  }
  await addDoc(collection(db, "testimonials"), {
    name: name.trim(),
    context: context?.trim() || "",
    area: area?.trim() || "",
    quote: quote.trim(),
    company: company || "", // honeypot — must stay empty, checked by rules
    createdAt: serverTimestamp(),
  });
}

export async function deleteTestimonial(id) {
  if (!isFirebaseConfigured) return;
  await deleteDoc(doc(db, "testimonials", id));
}

/* ---------------- Blog posts ---------------- */

/** Public: published posts only, newest first.
    Sorted client-side rather than with a Firestore orderBy — an
    equality filter (published==true) combined with orderBy on a
    different field (date) needs a composite index in Firestore, and
    an unbuilt index fails the query silently. Post counts here are
    small, so client-side sort avoids that dependency entirely. */
export function subscribePublishedPosts(onData) {
  if (!isFirebaseConfigured) {
    onData([]);
    return () => {};
  }
  const q = query(collection(db, "blogPosts"), where("published", "==", true));
  return onSnapshot(
    q,
    (snap) => {
      const posts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      posts.sort((a, b) => new Date(b.date) - new Date(a.date));
      onData(posts);
    },
    () => onData([])
  );
}

/** Admin: every post, published or draft. */
export function subscribeAllPosts(onData) {
  if (!isFirebaseConfigured) {
    onData([]);
    return () => {};
  }
  const q = query(collection(db, "blogPosts"), orderBy("date", "desc"));
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => onData([])
  );
}

/** Single published post by slug — used by the article page. */
export async function fetchPost(slug) {
  if (!isFirebaseConfigured) return null;
  const snap = await getDoc(doc(db, "blogPosts", slug));
  if (!snap.exists()) return null;
  const data = snap.data();
  if (!data.published) return null;
  return { id: snap.id, ...data };
}

export async function savePost(slug, data) {
  if (!isFirebaseConfigured) throw new Error("not-configured");
  await setDoc(doc(db, "blogPosts", slug), data, { merge: false });
}

export async function deletePost(slug) {
  if (!isFirebaseConfigured) return;
  await deleteDoc(doc(db, "blogPosts", slug));
}

/* ---------------- Site settings (doctor details) ---------------- */

export function subscribeSiteSettings(onData) {
  if (!isFirebaseConfigured) {
    onData(null);
    return () => {};
  }
  return onSnapshot(
    doc(db, "siteSettings", "main"),
    (snap) => onData(snap.exists() ? snap.data() : null),
    () => onData(null)
  );
}

export async function saveSiteSettings(data) {
  if (!isFirebaseConfigured) throw new Error("not-configured");
  await setDoc(doc(db, "siteSettings", "main"), data, { merge: true });
}
